import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { mcpCall } from '../../lib/mcp';
import { useMcpTools } from '../../lib/useMcpTools';
import { type Column } from '../RecordsTable/RecordsTable';
import { SkuDetail } from './SkuDetail';
import './productsCatalog.css';

/** Physical size joined client-side (size_merv_id → size_mervs.size_id → sizes). */
export interface SizeRow {
  id: number;
  length_in: number;
  width_in: number;
  depth_in: number;
  nominal_label: string;
  status: 'active' | 'inactive';
}

export interface SkuRow {
  id: number;
  size_merv_id: number;
  pack_count: number;
  sku_code: string;
  upc: string | null;
  amazon_asin: string | null;
  shopify_id: string | null;
  status: 'active' | 'inactive';
  price?: number | string | null;
  compare_at_price?: number | string | null;
  /** Shopify product image URL. Not returned by listSkus yet — see ProductThumb / the flagged
   *  cabana-mcp dependency. Undefined today, so thumbnails render the placeholder. */
  image_url?: string | null;
  /** Joined client-side from listSizeMervs (size_merv_id → merv); null if unmapped. */
  merv?: number | null;
  /** Joined client-side via size_mervs.size_id → sizes; null if unmapped. */
  size?: SizeRow | null;
}

interface SizeMervRow {
  id: number;
  size_id: number;
  merv: number;
  status: 'active' | 'inactive';
}

interface ProductsCatalogProps {
  title: string;
  intro?: string;
  columns: Column<SkuRow>[];
  rowKey: (row: SkuRow, index: number) => string | number;
  emptyMessage: string;
  toolbar?: ReactNode;
  actions?: { header: string; render: (row: SkuRow) => ReactNode };
}

// The MERV tiers the schema allows (productConfig.ts), in display order. Each has a
// matching --color-merv-N token in styles/variables.css used for the section accent.
const MERV_TIERS = [5, 8, 11, 13] as const;
const UNASSIGNED = 'unassigned';

type StatusFilter = 'all' | 'active' | 'inactive';
type PackFilter = 'all' | '1' | '2' | '4' | '6';
type ListingFilter = 'all' | 'missing_amazon' | 'listed_both';
type PriceFilter = 'all' | 'has_price' | 'no_price';

/** price arrives as a numeric string ("30.00"), number, null, or undefined. */
const hasPrice = (v: number | string | null | undefined): boolean => {
  if (v == null || v === '') return false;
  return Number.isFinite(Number(v));
};

const matchesSearch = (row: SkuRow, q: string): boolean => {
  if (!q) return true;
  const needle = q.toLowerCase();
  return [row.sku_code, row.upc, row.amazon_asin, row.shopify_id].some(
    (f) => f != null && f.toLowerCase().includes(needle),
  );
};

interface FilterState {
  status: StatusFilter;
  pack: PackFilter;
  listing: ListingFilter;
  price: PriceFilter;
}

const matchesFilters = (row: SkuRow, f: FilterState): boolean => {
  if (f.status !== 'all' && row.status !== f.status) return false;
  if (f.pack !== 'all' && row.pack_count !== Number(f.pack)) return false;
  if (f.listing === 'missing_amazon' && row.amazon_asin) return false;
  if (f.listing === 'listed_both' && (!row.shopify_id || !row.amazon_asin)) return false;
  if (f.price === 'has_price' && !hasPrice(row.price)) return false;
  if (f.price === 'no_price' && hasPrice(row.price)) return false;
  return true;
};

const sectionAccent = (key: number | typeof UNASSIGNED): string =>
  key === UNASSIGNED ? 'var(--border-default)' : `var(--color-merv-${key})`;

const sectionLabel = (key: number | typeof UNASSIGNED): string =>
  key === UNASSIGNED ? 'Unassigned' : `MERV ${key}`;

export function ProductsCatalog({
  title,
  intro,
  columns,
  rowKey,
  emptyMessage,
  toolbar,
  actions,
}: ProductsCatalogProps) {
  const { can } = useMcpTools();
  // History (audit_log) is a separate read tool a role may not have — gate it so the
  // detail panel hides that section gracefully rather than erroring.
  const canAudit = can('listAuditLog');

  // Keep the listSkus query key prefix (['mcp','listSkus']) so Products' refreshSkus()
  // invalidation still refetches this list after a price edit / push / sync.
  const skusQ = useQuery({
    queryKey: ['mcp', 'listSkus', { limit: 200 }],
    queryFn: async () => (await mcpCall<{ skus: SkuRow[] }>('listSkus', { limit: 200 })).skus ?? [],
  });
  const mervQ = useQuery({
    queryKey: ['mcp', 'listSizeMervs', { limit: 500 }],
    queryFn: async () =>
      (await mcpCall<{ sizeMervs: SizeMervRow[] }>('listSizeMervs', { limit: 500 })).sizeMervs ?? [],
  });
  // Reference data for the expandable detail (nominal label + dimensions). Joined client-side.
  const sizesQ = useQuery({
    queryKey: ['mcp', 'listSizes', { limit: 500 }],
    queryFn: async () =>
      (await mcpCall<{ sizes: SizeRow[] }>('listSizes', { limit: 500 })).sizes ?? [],
  });

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [pack, setPack] = useState<PackFilter>('all');
  const [listing, setListing] = useState<ListingFilter>('all');
  const [price, setPrice] = useState<PriceFilter>('all');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // Enrich each SKU with its MERV and physical size, chaining the two reference maps:
  // sku.size_merv_id → size_mervs (merv + size_id) → sizes (nominal label + dimensions).
  const rows = useMemo<SkuRow[]>(() => {
    const skus = skusQ.data ?? [];
    const mervById = new Map<number, SizeMervRow>();
    for (const m of mervQ.data ?? []) mervById.set(m.id, m);
    const sizeById = new Map<number, SizeRow>();
    for (const s of sizesQ.data ?? []) sizeById.set(s.id, s);
    return skus.map((s) => {
      const sm = mervById.get(s.size_merv_id);
      return {
        ...s,
        merv: sm?.merv ?? null,
        size: sm ? sizeById.get(sm.size_id) ?? null : null,
      };
    });
  }, [skusQ.data, mervQ.data, sizesQ.data]);

  const filterState: FilterState = { status, pack, listing, price };
  const filtered = useMemo(
    () => rows.filter((r) => matchesSearch(r, search) && matchesFilters(r, filterState)),
    [rows, search, status, pack, listing, price],
  );

  // Group filtered rows by MERV. Render known tiers in order, any other present values
  // numerically, then the Unassigned bucket last — each only if it has rows.
  const sections = useMemo(() => {
    const groups = new Map<number | typeof UNASSIGNED, SkuRow[]>();
    for (const r of filtered) {
      const key = r.merv == null ? UNASSIGNED : r.merv;
      const bucket = groups.get(key);
      if (bucket) bucket.push(r);
      else groups.set(key, [r]);
    }
    const presentTiers = [...groups.keys()].filter((k): k is number => k !== UNASSIGNED);
    const ordered = presentTiers.sort((a, b) => {
      const ia = (MERV_TIERS as readonly number[]).indexOf(a);
      const ib = (MERV_TIERS as readonly number[]).indexOf(b);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a - b;
    });
    const keys: Array<number | typeof UNASSIGNED> = [...ordered];
    if (groups.has(UNASSIGNED)) keys.push(UNASSIGNED);
    return keys.map((key) => ({ key, rows: groups.get(key)! }));
  }, [filtered]);

  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleRow = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // sizesQ feeds only the expandable detail; don't block the table on it or surface its error as
  // a full-page error — the detail degrades to MERV + pack if sizes are unavailable.
  const isPending = skusQ.isPending || mervQ.isPending;
  const isError = skusQ.isError || mervQ.isError;
  // Any in-flight fetch across the catalog's queries — drives the Refresh button's busy state.
  const isFetching = skusQ.isFetching || mervQ.isFetching || sizesQ.isFetching;
  const errorMsg = ((skusQ.error ?? mervQ.error) as Error | null)?.message;
  const retry = () => {
    void skusQ.refetch();
    void mervQ.refetch();
    void sizesQ.refetch();
  };

  const totalCount = rows.length;
  const filteredCount = filtered.length;

  return (
    <section className="records">
      <header className="records__head">
        <div className="records__head-text">
          <h1 className="records__title">{title}</h1>
          {intro && <p className="records__intro">{intro}</p>}
        </div>
        {/* Refresh is a non-destructive re-pull of the catalog — distinct from the outbound
            Push / Sync write actions, and stacked above them so it never reads as primary. */}
        <div className="catalog__header-actions">
          <button
            type="button"
            className="catalog__refresh"
            onClick={retry}
            disabled={isFetching}
            title="Reload the catalog from the database"
          >
            <span
              className={`catalog__refresh-icon${isFetching ? ' catalog__refresh-icon--spinning' : ''}`}
              aria-hidden="true"
            >
              ↻
            </span>
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
          {toolbar}
        </div>
      </header>

      {/* Controls stay visible once data has loaded so the operator can always clear filters. */}
      {!isPending && !isError && totalCount > 0 && (
        <div className="catalog__controls">
          <input
            type="search"
            className="catalog__search"
            placeholder="Search SKU code, UPC, ASIN, or Shopify ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search products"
          />
          <div className="catalog__filters">
            <label className="catalog__filter">
              <span className="catalog__filter-label">Status</span>
              <select
                className="catalog__select"
                value={status}
                onChange={(e) => setStatus(e.target.value as StatusFilter)}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
            <label className="catalog__filter">
              <span className="catalog__filter-label">Pack</span>
              <select
                className="catalog__select"
                value={pack}
                onChange={(e) => setPack(e.target.value as PackFilter)}
              >
                <option value="all">All</option>
                <option value="1">1-pack</option>
                <option value="2">2-pack</option>
                <option value="4">4-pack</option>
                <option value="6">6-pack</option>
              </select>
            </label>
            <label className="catalog__filter">
              <span className="catalog__filter-label">Listing</span>
              <select
                className="catalog__select"
                value={listing}
                onChange={(e) => setListing(e.target.value as ListingFilter)}
              >
                <option value="all">All</option>
                <option value="missing_amazon">Missing Amazon</option>
                <option value="listed_both">Listed on both</option>
              </select>
            </label>
            <label className="catalog__filter">
              <span className="catalog__filter-label">Price</span>
              <select
                className="catalog__select"
                value={price}
                onChange={(e) => setPrice(e.target.value as PriceFilter)}
              >
                <option value="all">All</option>
                <option value="has_price">Has price</option>
                <option value="no_price">No price</option>
              </select>
            </label>
          </div>
        </div>
      )}

      <div className="records__panel">
        {isPending && (
          <div className="records__state" role="status" aria-live="polite">
            <span className="records__spinner" aria-hidden="true" />
            Loading…
          </div>
        )}

        {isError && (
          <div className="records__state records__state--error" role="alert">
            <strong>Couldn’t load this data.</strong>
            {errorMsg && <span className="records__state-detail">{errorMsg}</span>}
            <button type="button" className="records__retry" onClick={retry}>
              Try again
            </button>
          </div>
        )}

        {!isPending && !isError && totalCount === 0 && (
          <div className="records__state">{emptyMessage}</div>
        )}

        {!isPending && !isError && totalCount > 0 && filteredCount === 0 && (
          <div className="records__state">No products match the current search and filters.</div>
        )}

        {!isPending && !isError && sections.length > 0 && (
          <div className="catalog__sections">
            {sections.map(({ key, rows: groupRows }) => {
              const keyStr = String(key);
              const isCollapsed = collapsed.has(keyStr);
              return (
                <div className="catalog__section" key={keyStr}>
                  <button
                    type="button"
                    className="catalog__section-head"
                    style={{ borderLeftColor: sectionAccent(key) }}
                    aria-expanded={!isCollapsed}
                    onClick={() => toggle(keyStr)}
                  >
                    <span className="catalog__section-chevron" aria-hidden="true">
                      {isCollapsed ? '▸' : '▾'}
                    </span>
                    <span
                      className="catalog__section-dot"
                      style={{ backgroundColor: sectionAccent(key) }}
                      aria-hidden="true"
                    />
                    <span className="catalog__section-title">{sectionLabel(key)}</span>
                    <span className="catalog__section-count">{groupRows.length}</span>
                  </button>

                  {!isCollapsed && (
                    <div className="records__scroll">
                      <table className="records__table">
                        <thead>
                          <tr>
                            <th className="catalog__expand-col" aria-hidden="true" />
                            {columns.map((c) => (
                              <th key={c.key} className={c.numeric ? 'records__th--num' : undefined}>
                                {c.header}
                              </th>
                            ))}
                            {actions && <th className="records__th--actions">{actions.header}</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {groupRows.map((row, i) => {
                            const isOpen = expanded.has(row.id);
                            const detailId = `sku-detail-${row.id}`;
                            return (
                              <Fragment key={rowKey(row, i)}>
                                <tr
                                  className={`catalog__row${isOpen ? ' catalog__row--open' : ''}${row.status === 'inactive' ? ' records__row--inactive' : ''}`}
                                  onClick={() => toggleRow(row.id)}
                                >
                                  <td className="catalog__expand-col">
                                    <button
                                      type="button"
                                      className="catalog__expand-btn"
                                      aria-expanded={isOpen}
                                      aria-controls={detailId}
                                      aria-label={isOpen ? `Hide details for ${row.sku_code}` : `Show details for ${row.sku_code}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleRow(row.id);
                                      }}
                                    >
                                      <span className="catalog__expand-chevron" aria-hidden="true">
                                        ▸
                                      </span>
                                    </button>
                                  </td>
                                  {columns.map((c) => (
                                    <td key={c.key} className={c.numeric ? 'records__td--num' : undefined}>
                                      {c.render
                                        ? c.render(row)
                                        : String((row as unknown as Record<string, unknown>)[c.key] ?? '')}
                                    </td>
                                  ))}
                                  {actions && (
                                    // Stop row-click from toggling the expander when using a row action.
                                    <td
                                      className="records__td--actions"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {actions.render(row)}
                                    </td>
                                  )}
                                </tr>
                                {isOpen && (
                                  <tr className="catalog__detail-row" id={detailId}>
                                    <td colSpan={columns.length + 1 + (actions ? 1 : 0)}>
                                      <SkuDetail row={row} canAudit={canAudit} />
                                    </td>
                                  </tr>
                                )}
                              </Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
